import React, { useState, useContext, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ref, set } from 'firebase/database';
import { database } from '../config/firebase';
import { AuthContext } from '../contexts/AuthContext';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';

interface TeamInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  teamName: string;
}

const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
];

const TeamInviteModal: React.FC<TeamInviteModalProps> = ({ 
  isOpen, 
  onClose, 
  teamId, 
  teamName 
}) => {
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;
  
  // Use refs to maintain state independently of parent re-renders
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'editor' | 'viewer'>('viewer');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Use ref for email input to maintain focus
  const emailInputRef = useRef<HTMLInputElement>(null);
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setRole('viewer');
      setError('');
      setSuccess('');
      setInviting(false);
      // Focus the email input when modal opens
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);
  
  // Don't render if not open
  if (!isOpen) return null;

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Clear any previous errors when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRole(e.target.value as 'admin' | 'editor' | 'viewer');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter an email address');
      emailInputRef.current?.focus();
      return;
    }
    
    if (!currentUser?.email) {
      setError('You must be logged in to send invites');
      return;
    }
    
    setInviting(true);
    setError('');
    setSuccess('');
    
    try {
      const inviteId = uuidv4();
      const inviteRef = ref(database, `invites/${inviteId}`);
      
      await set(inviteRef, {
        email: email.trim(),
        role,
        teamId,
        teamName,
        inviter: currentUser.email,
        status: 'pending',
        createdAt: Date.now(),
      });

      setSuccess(`Invite sent to ${email}!`);
      setEmail('');
      setRole('viewer');
      
      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (err: any) {
      console.error('Invite error:', err);
      setError('Failed to send invite. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  const handleClose = () => {
    if (!inviting) {
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 font-['Poppins']">
            Invite Member
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={inviting}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Input */}
          <div>
            <label 
              htmlFor="invite-email"
              className="block text-sm font-medium text-gray-700 mb-2 font-['Inter']"
            >
              Email Address
            </label>
            <input
              id="invite-email"
              ref={emailInputRef}
              type="email"
              value={email}
              onChange={handleEmailChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 font-['Inter']"
              placeholder="Enter email address"
              disabled={inviting}
              required
              autoComplete="email"
            />
          </div>

          {/* Role Select */}
          <div>
            <label 
              htmlFor="invite-role"
              className="block text-sm font-medium text-gray-700 mb-2 font-['Inter']"
            >
              Role
            </label>
            <select
              id="invite-role"
              value={role}
              onChange={handleRoleChange}
              disabled={inviting}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 font-['Inter']"
            >
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">
              {success}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={inviting}
              className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={inviting || !email.trim()}
              className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
            >
              {inviting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Inviting...
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  Send Invite
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamInviteModal;